import React, { useState } from "react";
import {
  BottomSheet,
  Button,
  DateField,
  DropdownField,
  Modal,
  useIsMobile,
} from "../../../components/ui";
import {
  DURATION_OPTIONS,
  TIME_OPTIONS,
  clashWith,
  durationOf,
  timeOptionsWithBookings,
  durationOptionLabel,
  isWhenComplete,
  nextSlotAfter,
  startOfDay,
  startsAtOf,
  whenErrors,
  type AppointmentWhen,
} from "./appointmentFields";
import { useDayAppointments } from "../hooks/useDayAppointments";
import { useMeetingApps } from "../hooks/useMeetingApps";
// Deep imports rather than the brand barrel, which pulls in the whole My brand
// screen and its dialogs; this needs the saved details and the one line that
// reads an address out of them. Same pair the Schedule dialog takes.
import { useBrandDetails } from "../../brand/hooks/useBrandDetails";
import { brandAddressLine } from "../../brand/brandAddress";
import AppointmentAddressNote from "./AppointmentAddressNote";
import AppointmentSummaryCard from "./AppointmentSummaryCard";
import MeetingAppMark from "./MeetingAppMark";
import type {
  ClientAppointment,
  RescheduleAppointment,
} from "../types/appointments.types";

/**
 * `2026-09-26T11:00:00Z` as the `DD/MM/YYYY` the date field holds and the
 * `HH:MM` the time list is keyed by — the appointment's current slot, put back
 * into the controls so moving it by half an hour is one change rather than
 * three.
 */
const whenOf = (appointment: ClientAppointment): AppointmentWhen => {
  const start = new Date(appointment.startsAt);
  const pad = (part: number) => String(part).padStart(2, "0");

  // An unparseable start leaves the fields empty rather than filled with
  // NaNs — the dialog is then simply one that has to be answered from scratch.
  if (Number.isNaN(start.getTime())) {
    return { date: "", time: "", duration: "" };
  }

  return {
    date: `${pad(start.getDate())}/${pad(start.getMonth() + 1)}/${start.getFullYear()}`,
    // Only lands on the list when the appointment sits on a quarter hour. One
    // that does not — off a record written when the list ran in half hours, or
    // a backend that books to the minute — leaves the field showing its
    // placeholder, so the dropdown is given the held value as an option of its
    // own. See `timeOptions` below.
    time: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
    // Lands on the list only for a length that is one of the eight; one that
    // is not still shows, because `durationOptions` puts it on the list. A
    // record with no length at all leaves the field on its placeholder.
    duration: appointment.durationMinutes
      ? String(appointment.durationMinutes)
      : "",
  };
};

/**
 * The time list with the appointment's own slot on it, wherever that falls.
 *
 * `HH:MM` sorts as text exactly as it does as a time, so an off-grid slot
 * lands where it belongs in the day rather than at one end of the list.
 */
const timeOptions = (held: string) => {
  if (!held || TIME_OPTIONS.some((option) => option.value === held)) {
    return TIME_OPTIONS;
  }

  const hours = Number(held.slice(0, 2));
  const suffix = hours < 12 ? "AM" : "PM";
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  const option = {
    value: held,
    label: `${String(shown).padStart(2, "0")}:${held.slice(3)} ${suffix}`,
  };

  const before = TIME_OPTIONS.filter((slot) => slot.value < held);
  return [...before, option, ...TIME_OPTIONS.slice(before.length)];
};

/**
 * The length list with the appointment's own length on it, wherever it falls.
 *
 * Same trick as `timeOptions` above and for the same reason: the eight lengths
 * are what a new appointment is booked at, not what every stored one is. A
 * record at 25 minutes would otherwise open on a placeholder, and a dialog
 * that shows no length is one that cannot be saved without picking a new one.
 * Sorted numerically so it lands between the lengths it sits between.
 */
const durationOptions = (held: number) => {
  const value = String(held);
  if (!held || DURATION_OPTIONS.some((option) => option.value === value)) {
    return DURATION_OPTIONS;
  }

  return [...DURATION_OPTIONS, { value, label: durationOptionLabel(held) }].sort(
    (a, b) => Number(a.value) - Number(b.value)
  );
};

export interface RescheduleAppointmentModalProps {
  /** The appointment being moved — quoted at the top, and what the fields open on. */
  appointment: ClientAppointment;
  /** Set while the move is with the server. */
  busy?: boolean;
  onClose: () => void;
  onConfirm: (input: RescheduleAppointment) => void;
}

/**
 * Moves an appointment to a new day, time or length.
 *
 * Its own dialog rather than the schedule one reopened, because the two are
 * different acts: this one cannot change whether the appointment is online or
 * offline, nor the link a call happens on — a call that moves to Thursday is
 * the same call — and it opens on the slot being moved rather than on nothing,
 * since the usual change is a small one made against what is already there.
 *
 * A slot and a length, and nothing else. The room used to be the exception, on
 * the reading that moving an in-person sitting often moves the room with it;
 * it is stated here now, the way the Schedule dialog states it, because an
 * address typed per appointment is one the practice's own record never learns
 * about — see `AppointmentAddressNote`.
 *
 * The card at the top is what makes that safe: the fields say where it is
 * going and the strip says where it is now, so a dialog opened on the wrong
 * row is obvious before anything is saved.
 *
 * The new slot has to be ahead, the same as a new booking's. Moving an
 * appointment into last week is not a correction anybody makes on purpose.
 */
const RescheduleAppointmentModal: React.FC<RescheduleAppointmentModalProps> = ({
  appointment,
  busy = false,
  onClose,
  onConfirm,
}) => {
  /**
   * Which shell the three fields are put in — a centred panel on a wide
   * screen, a sheet up from the bottom edge on a phone. The same call the
   * Schedule dialog makes, and for the same reason: a form answered with a
   * thumb wants its buttons where the thumb already is.
   */
  const isMobile = useIsMobile();

  const [when, setWhen] = useState<AppointmentWhen>(() => whenOf(appointment));

  /** Only an in-person appointment has a room; an online one has a link. */
  const offline = appointment.type === "Offline";

  // Where the practice sees people, for an appointment that has no address of
  // its own on record. "" while it is still in flight, and for a practice that
  // has written none down.
  const { loaded: brand } = useBrandDetails();
  const practiceAddress = brand ? brandAddressLine(brand) : "";
  // The appointment's own room where it has one, and the practice's where it
  // does not. Its own first, because that is where the sitting actually is: a
  // booking made before the practice moved is still being held at the old
  // address, and quoting the new one here would say this dialog had moved it.
  const address = appointment.address || practiceAddress || null;
  // Only ever read for the mark on the pill below: which app is making the
  // links. The read is the one the Schedule dialog and the Integrate apps card
  // already share, so this costs a cache hit rather than a request.
  const { activeApp } = useMeetingApps();

  // Read once when the dialog opens — see the same line on the schedule one.
  const [now] = useState(() => new Date());

  const set = (patch: Partial<AppointmentWhen>) =>
    setWhen((current) => ({ ...current, ...patch }));

  // What the chosen day already holds. The appointment being moved is left
  // out of the check — it opens on its own slot, and a dialog reporting a
  // clash with itself could never be saved.
  const day = useDayAppointments(when.date);
  const clash = clashWith(when, day.booked, appointment.id);
  // Closed off on the list rather than argued with after the fact — see
  // `timeOptionsWithBookings`. Built on `timeOptions(when.time)` rather than
  // on the bare list, so an off-grid slot this appointment already sits on is
  // still there to be seen.
  const times = timeOptionsWithBookings(
    timeOptions(when.time),
    when.date,
    durationOf(when),
    day.booked,
    appointment.id
  );

  const startsAt = startsAtOf(when);
  const moved = !!startsAt && startsAt !== appointment.startsAt;
  const relengthed = durationOf(when) !== appointment.durationMinutes;

  // Closed while the fields say nothing new: saving an appointment back onto
  // the slot it is already in is a write that tells the client nothing and
  // leaves the practitioner unsure whether the dialog took.
  const errors = whenErrors(when, now);
  const ready =
    isWhenComplete(when) &&
    !errors.date &&
    !errors.time &&
    // A clash closes the button; a check still in flight does not — see the
    // same line on the schedule dialog.
    !clash &&
    // The room is no longer one of them: it is stated here rather than asked
    // for, so a slot and a length are the only two things this dialog can
    // change and therefore the only two that can open the button.
    (moved || relengthed);

  const handleConfirm = () => {
    if (!startsAt || busy) return;
    onConfirm({
      startsAt,
      durationMinutes: durationOf(when),
      // Whatever the panel just said, for an in-person one: usually the
      // appointment's own address sent back unchanged, and the practice's
      // where the record had none — so what was read above is what ends up
      // stored. Left off entirely for an online one, which has no room.
      address: offline ? address ?? "" : undefined,
    });
  };

  /** The same two buttons whichever shell is drawn. */
  const actions = (
    <>
      <Button variant="secondary" size="md" disabled={busy} onClick={onClose}>
        Cancel
      </Button>
      <Button size="md" loading={busy} disabled={!ready} onClick={handleConfirm}>
        Update &amp; save
      </Button>
    </>
  );

  const fields = (
    <div className="flex flex-col gap-4">
      {/* Where it stands now, so the three fields under it read as a
          change from something rather than as an answer out of nowhere.

          The same card the Complete dialog quotes its appointment in, in
          the louder of its two tones. It used to be a line of text here
          because the design drew it as one; it is the card now, and sharing
          it is what keeps "an appointment, quoted" looking like one thing. */}
      <div>
        {/* Said out loud, because the card underneath is not an answer
            anybody gave this dialog — it is the slot being moved, and a
            panel that opens with an appointment in a box and no word about
            why reads as a field that has already been filled in. */}
        <p className="mb-1.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          Currently scheduled
        </p>
        <AppointmentSummaryCard
          appointment={appointment}
          tone="brand"
          /* Which kind it is, because it is the one thing this dialog cannot
             change and the thing that decides what the last field asks for.

             An online one carries the mark of the app hosting it, the same
             way the Schedule dialog's Online button does — so moving a call
             says where the client is being sent, rather than leaving that to
             be discovered when the new invitation lands. Nothing is drawn
             while no app is connected: there is no link being made, so there
             is nothing to name. */
          trailing={
            <span className="flex items-center gap-1.5 rounded-full bg-[#E6FAFB] px-2.5 py-1 text-[12px] text-[#007E8C]">
              {!offline && activeApp && (
                <MeetingAppMark app={activeApp} size={16} />
              )}
              {appointment.type}
            </span>
          }
        />
      </div>

      {/* The whole width, as on the schedule dialog — the two share a shape
          so that moving an appointment reads as the same form as booking
          one, with the strip above saying what is being moved. */}
      <DateField
        label="New date"
        value={when.date}
        onValueChange={(value) => set({ date: value })}
        placeholder="DD/MM/YY"
        // Today onwards, as on the schedule dialog. Reschedule is only ever
        // offered on an appointment still ahead, so the slot this opens on
        // is never itself behind the limit.
        minDate={startOfDay(now)}
        error={errors.date}
        disabled={busy}
      />

      <div className="grid grid-cols-2 gap-4">
        <DropdownField
          label="Time"
          value={when.time}
          placeholder="11:00 AM"
          options={times}
          // The whole day at once — see the same field on the schedule
          // dialog. `+ 1` for the off-grid slot `timeOptions` may have
          // added, so that one is built too.
          optionPageSize={TIME_OPTIONS.length + 1}
          // Opened at the next quarter hour rather than at midnight — it
          // scrolls the list and answers nothing. See `nextSlotAfter`.
          openAt={nextSlotAfter(now)}
          onValueChange={(value) => set({ time: value })}
          error={errors.time}
          disabled={busy}
        />
        <DropdownField
          label="Select duration"
          value={when.duration}
          placeholder="30 min"
          // The appointment's own length where it is not one of the eight —
          // a record written before this list existed, or one booked to the
          // minute — so the field shows what it holds rather than its
          // placeholder. See `durationOptions`.
          options={durationOptions(appointment.durationMinutes)}
          // Lengthening an appointment can run it over the next one, which
          // no list of start times can refuse in advance. The time is let go
          // rather than left sitting there greyed out and unsaveable: the
          // list it came from has already been redrawn at the new length,
          // and picking again off it is the one move left.
          onValueChange={(value) => {
            const next = { ...when, duration: value };
            set(
              clashWith(next, day.booked, appointment.id)
                ? { duration: value, time: "" }
                : { duration: value }
            );
          }}
          disabled={busy}
        />
      </div>

      {/* Only where there is a room to name. An online appointment is moved
          without touching its link, which this dialog has never offered.

          Stated rather than asked for, which is the Schedule dialog's
          arrangement and now this one's — the same block, drawn by the same
          component. It was a box here, on the reading that moving an
          in-person sitting is the act that most often moves the room with
          it. That reading cost more than it bought: an address typed into
          one appointment is an address that does not match the practice's
          and that nothing else will ever pick up, so the dialog that moves
          an appointment became the one place in the app where the practice
          address could quietly fork. It is changed where it is kept. */}
      {offline && (
        <AppointmentAddressNote
          address={address}
          // Badged only where the line came from the practice rather than
          // from this appointment — see `isDefault`.
          isDefault={!appointment.address}
          onLeave={onClose}
        />
      )}
    </div>
  );

  // Not while it is with the server, on either shell: the move is going
  // through either way, and a dialog closed over it would leave nothing to say
  // so.
  const handleClose = () => !busy && onClose();

  if (isMobile) {
    return (
      // A press on the dimming above it closes it, as on the Schedule sheet
      // and as the dialog below has always done with its backdrop. Held only
      // while the move is with the server, by `handleClose`.
      <BottomSheet
        open
        title="Reschedule appointment"
        onClose={handleClose}
        footer={actions}
      >
        <div className="px-5 pb-2 pt-2">{fields}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open
      title="Reschedule appointment"
      onClose={handleClose}
      className="max-w-[440px]"
      footer={actions}
    >
      {fields}
    </Modal>
  );
};

export default RescheduleAppointmentModal;
